import type { SpinResponse } from "../contracts/SpinResponse";
import type { RoundSnapshot } from "../contracts/RoundSnapshot";
import { ProtocolError } from "./ProtocolError";

/**
 * A lightweight validation error with a precise path, so you can quickly
 * find the problematic field in large round snapshots.
 */
export class SpinResponseValidationError extends ProtocolError {
  public readonly path: string;

  public constructor(path: string, message: string) {
    super(`SpinResponse validation failed at "${path}": ${message}`);
    this.name = "SpinResponseValidationError";
    this.path = path;
  }
}

/**
 * Validates the SpinResponse envelope (schemaVersion + roundSnapshot + the balance snapshot's amount, when sent).
 * It intentionally does not validate game-specific details.
 */
export function validateSpinResponseOrThrow(spinResponse: SpinResponse): void {
  if (!spinResponse) {
    throw new SpinResponseValidationError("spinResponse", "Value is null/undefined.");
  }

  if (typeof spinResponse.schemaVersion !== "string" || spinResponse.schemaVersion.length === 0) {
    throw new SpinResponseValidationError("schemaVersion", "Must be a non-empty string.");
  }

  // roundSnapshot is required
  const roundSnapshot = spinResponse.roundSnapshot as RoundSnapshot | undefined;
  if (!roundSnapshot) {
    throw new SpinResponseValidationError("roundSnapshot", "Missing roundSnapshot.");
  }

  // The coordinator puts this balance on the readout after `requestPlay` has launched the round's
  // replay, so a shape BalanceStore would refuse is refused here, before the launch, as a protocol
  // error — not thrown from under a presentation already driving the grid (decision 22; 2026-09-27 review #18).
  const balanceAmount: unknown = spinResponse.accountBalanceSnapshot?.balanceAmount;
  if (balanceAmount !== undefined && balanceAmount !== null && (typeof balanceAmount !== "string" || !/^\d+$/.test(balanceAmount))) {
    throw new SpinResponseValidationError("accountBalanceSnapshot.balanceAmount", `Must be a non-negative integer string in minor units, got ${JSON.stringify(balanceAmount)}.`);
  }
}